import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SEVERITY, type Role } from '@soc/shared';

/** Shared building blocks for the admin screens (Users, Organizations, Projects). */

export const card: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 14,
};

export const inputStyle: CSSProperties = {
  width: '100%',
  background: 'var(--surface-card)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text-primary)',
  font: 'inherit',
  fontSize: 14,
  padding: '9px 12px',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--text-faint)',
  marginBottom: 6,
};

export const pillButton: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  padding: '4px 10px',
  cursor: 'pointer',
  color: 'var(--text-muted)',
};

export function primaryButtonStyle(enabled: boolean): CSSProperties {
  return {
    background: enabled ? 'var(--accent-grad)' : 'var(--surface-card)',
    color: enabled ? 'var(--accent-on)' : 'var(--text-dim)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    padding: '10px 18px',
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}

/** Full-screen themed wrapper with a header + back-to-dashboard link. */
export function AdminScreen({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="theme-noc"
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--surface-app)',
        color: 'var(--text-primary)',
        display: 'grid',
        gridTemplateRows: '60px 1fr',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: '0 22px',
          background: 'var(--surface-raised)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: 13 }}>
          ← Dashboard
        </Link>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 2 }}>{title}</span>
        {subtitle && <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

/** Left list pane + right form pane, the grammar shared by every admin screen. */
export function TwoPane({ list, form }: { list: ReactNode; form: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', minHeight: 0 }}>
      <div
        className="scrl"
        style={{
          borderRight: '1px solid var(--border)',
          background: 'var(--surface-panel)',
          overflowY: 'auto',
          padding: 16,
        }}
      >
        {list}
      </div>
      <div className="scrl" style={{ overflowY: 'auto', padding: 24, minHeight: 0 }}>
        {form}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={labelStyle}>{label}</span>
      {children}
      {error ? (
        <span style={{ display: 'block', fontSize: 12, color: SEVERITY.CRITICAL.color, marginTop: 5 }}>
          {error}
        </span>
      ) : hint ? (
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-faint)', marginTop: 5 }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>{children}</div>
  );
}

export function ListCount({ n, noun }: { n: number; noun: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        letterSpacing: 1,
        color: 'var(--text-faint)',
        textTransform: 'uppercase',
        marginBottom: 12,
      }}
    >
      {n} {noun}
      {n === 1 ? '' : 's'}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: SEVERITY.CRITICAL.chip,
        border: `1px solid ${SEVERITY.CRITICAL.color}`,
        borderRadius: 8,
        color: 'var(--text-primary)',
        fontSize: 13,
        padding: '9px 12px',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

const ROLE_STYLE: Record<Role, { color: string; bg: string }> = {
  user: { color: 'var(--text-muted)', bg: 'rgba(120,160,200,0.1)' },
  admin: { color: 'var(--accent)', bg: 'rgba(0,229,255,0.12)' },
  super_admin: { color: 'var(--ok)', bg: 'rgba(41,224,168,0.12)' },
};

/**
 * Human label for a role. The stored value `user` is surfaced as "Viewer"
 * everywhere in the UI (relabel only — the role value is unchanged).
 */
const ROLE_LABEL: Record<Role, string> = {
  user: 'Viewer',
  admin: 'Admin',
  super_admin: 'Super admin',
};

export function roleLabel(role: Role): string {
  return ROLE_LABEL[role] ?? role;
}

export function RoleBadge({ role }: { role: Role }) {
  const s = ROLE_STYLE[role];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.color}`,
        borderRadius: 20,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {roleLabel(role)}
    </span>
  );
}

/** Initials from a display name, for avatars. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
